/**
 * Filtros em cascata do seletor de centro de custo (ADR 0092): empresa → departamento → Master →
 * Gerencial → centro. Cada nível só mostra o que cabe nos de cima, pelos vínculos gravados (nunca
 * pelo nome). Trocar um nível de cima limpa os de baixo que deixaram de caber.
 */
import {
  costCenterDepartmentOf,
  eligibleCostCenters,
  type CostCenterEligibilityFilter,
  type CostCenterEligibilityNode,
  type CostCenterLevel,
} from '@loot/shared';

/** Cada centro, como vem de `GET /core/cost-centers/eligibility`. */
export interface EligibleCostCenter extends CostCenterEligibilityNode {
  name: string;
  legalEntityCode: string;
  departmentCode: string | null;
  departmentName: string | null;
  managerUserId: string | null;
  managerName: string | null;
  substituteUserId: string | null;
  substituteName: string | null;
}

export interface CascadeSelection {
  legalEntityId: string;
  departmentId: string;
  masterId: string;
  managerialId: string;
}

export const EMPTY_CASCADE: CascadeSelection = {
  legalEntityId: '',
  departmentId: '',
  masterId: '',
  managerialId: '',
};

/** Regras da finalidade: níveis aceitos, legado sem nível e centros já gravados. */
export interface CascadeRules {
  levels?: readonly CostCenterLevel[] | null;
  allowUnleveled?: boolean;
  keepIds?: ReadonlySet<string>;
  onDate?: string;
}

export interface CascadeOptions {
  companies: { id: string; code: string }[];
  departments: { id: string; label: string }[];
  masters: EligibleCostCenter[];
  managerials: EligibleCostCenter[];
  centers: EligibleCostCenter[];
}

/** O que cada nível do seletor oferece, dado o que já foi escolhido nos de cima. */
export function cascadeOptions(
  nodes: readonly EligibleCostCenter[],
  sel: CascadeSelection,
  rules: CascadeRules = {},
): CascadeOptions {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const base: CostCenterEligibilityFilter = { onDate: rules.onDate, keepIds: rules.keepIds };
  const company = sel.legalEntityId || null;
  const companies = [
    ...new Map(
      eligibleCostCenters(nodes, base).map((n) => [n.legalEntityId, n.legalEntityCode]),
    ).entries(),
  ]
    .map(([id, code]) => ({ id, code }))
    .sort((a, b) => a.code.localeCompare(b.code));
  const inCompany = eligibleCostCenters(nodes, { ...base, legalEntityId: company });
  const deptLabel = new Map<string, string>();
  for (const n of nodes) {
    if (n.departmentId && !deptLabel.has(n.departmentId)) {
      deptLabel.set(n.departmentId, n.departmentName ?? n.departmentCode ?? n.departmentId);
    }
  }
  const departments = [
    ...new Set(inCompany.map((n) => costCenterDepartmentOf(n.id, byId)).filter(Boolean)),
  ]
    .map((id) => ({ id: id!, label: deptLabel.get(id!) ?? id! }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const withDept = { ...base, legalEntityId: company, departmentId: sel.departmentId || null };
  const masters = eligibleCostCenters(nodes, {
    ...withDept,
    levels: ['master'],
    allowUnleveled: false,
  });
  const managerials = eligibleCostCenters(nodes, {
    ...withDept,
    ancestorId: sel.masterId || null,
    levels: ['managerial'],
    allowUnleveled: false,
  });
  const centers = eligibleCostCenters(nodes, {
    ...withDept,
    ancestorId: sel.managerialId || sel.masterId || null,
    levels: rules.levels,
    allowUnleveled: rules.allowUnleveled,
  });
  return { companies, departments, masters, managerials, centers };
}

/**
 * Limpa o que deixou de caber depois de uma troca: Master fora do departamento, Gerencial fora
 * do Master, centro fora de tudo. Devolve a seleção e o centro (vazio se foi limpo).
 */
export function reconcileCascade(
  nodes: readonly EligibleCostCenter[],
  sel: CascadeSelection,
  value: string,
  rules: CascadeRules = {},
): { sel: CascadeSelection; value: string } {
  let next = { ...sel };
  let opts = cascadeOptions(nodes, next, rules);
  if (next.departmentId && !opts.departments.some((d) => d.id === next.departmentId)) {
    next = { ...next, departmentId: '', masterId: '', managerialId: '' };
    opts = cascadeOptions(nodes, next, rules);
  }
  if (next.masterId && !opts.masters.some((m) => m.id === next.masterId)) {
    next = { ...next, masterId: '', managerialId: '' };
    opts = cascadeOptions(nodes, next, rules);
  }
  if (next.managerialId && !opts.managerials.some((m) => m.id === next.managerialId)) {
    next = { ...next, managerialId: '' };
    opts = cascadeOptions(nodes, next, rules);
  }
  return { sel: next, value: value && opts.centers.some((c) => c.id === value) ? value : '' };
}

/** A seleção que mostra um centro já escolhido: a empresa dele, sem filtros abaixo. */
export function cascadeFor(
  nodes: readonly EligibleCostCenter[],
  value: string,
  legalEntityId?: string | null,
): CascadeSelection {
  const node = nodes.find((n) => n.id === value);
  return { ...EMPTY_CASCADE, legalEntityId: legalEntityId ?? node?.legalEntityId ?? '' };
}
